import MonthPeriod from "@/entities/month-period";
import {RouteLocationNormalized} from "vue-router";
import InvalidRouteParamError from './invalid-route-param-error';


/**
 * Object with the conversion result from all the parsers of this module.
 * It should only have 1 pair. The Key is the name of the route param and the
 * value is the parsed value.
 *
 * @example
 * This functions are expected to be used in the props property in the route
 * config object with the spread operator.
 * ```
 * {
 *    path: 'route-path',
 *    component: componentA,
 *    props: (route) => ({
 *      ...numericParser(route, "paramName1"),
 *      ...numericParser(route, "paramName2"),
 *      ...numericParser(route, "paramName3", "query"),
 *      ...monthPeriodParser(route, "paramName4", "param", "newNameParam"),
 *      ...monthPeriodParser(route, "paramName5"),
 *      ...nullableMonthPeriodParser(route, "paramName5"),
 *  }
 * ```
 */
export type ConversionPair<T> = Record<string, T>;

type ParamType = "param" | "query";

export type Parser<R> = (
  route: RouteLocationNormalized,
  param: string,
  type?: ParamType,
  nameAs?: string
) => ConversionPair<R>;

type ParserTemplate<R> = (paramVal: string, paramName: string) => R;

function requiredParserBuilder<R>(fn: ParserTemplate<R>): Parser<R> {
  return (
    route: RouteLocationNormalized,
    param: string,
    type: ParamType = "param",
    nameAs?: string
  ) => {
    const paramVal = type == "param" ?
      route.params[param] :
      route.query[param];
    if (typeof paramVal !== "string") {
      throw new InvalidRouteParamError(param, undefined, "Is not a parseable string.");
    }

    const result = fn(paramVal, param);
    const parsedPropName = nameAs == undefined ?
      param :
      nameAs;

    const paramRec: Record<string, R> = {};
    paramRec[parsedPropName] = result;
    return paramRec;
  }

}

function nullableParserBuilder<R>(fn: ParserTemplate<R>): Parser<R | null> {
  return (
    route: RouteLocationNormalized,
    param: string,
    type: ParamType = "param",
    nameAs?: string
  ) => {
    const paramVal = type == "param" ?
      route.params[param] :
      route.query[param];
    let result: R | null = null;
    if (paramVal !== undefined) {
      if (typeof paramVal !== "string") {
        throw new InvalidRouteParamError(param, undefined, "Is not a parseable string.");
      }
      if (paramVal !== "") {
        result = fn(paramVal, param);
      }
    }
    const parsedPropName = nameAs == undefined ?
      param :
      nameAs;
    const paramRec: Record<string, R | null> = {};
    paramRec[parsedPropName] = result;
    return paramRec;
  }

}


function numericParserTemplate(paramVal: string, param: string): number {
  const paramNumber = Number.parseInt(paramVal, 10);
  if (Number.isNaN(paramNumber)) {
    throw new InvalidRouteParamError(param, paramVal, "Is not a number.");
  }
  return paramNumber;
}

function monthPeriodParserTemplate(paramVal: string, param: string) {
  try {
    return MonthPeriod.fromMYStr(paramVal);
  } catch (e) {
    throw new InvalidRouteParamError(param, paramVal, "Is not a MonthPeriod.", e as Error);
  }
}

/**
 * Converts a route param to a number.
 *
 * @throws InvalidRouteParamError If the conversion fails.
 */
export const numericParser = requiredParserBuilder<number>(numericParserTemplate);


/**
 * Converts a route param to a MonthPeriod.
 *
 * @remarks Since this app is used by spanish speakers, this parser expects
 * `m-Y` formated str.
 *
 * @throws InvalidRouteParamError If the conversion fails.
 */
export const monthPeriodParser = requiredParserBuilder<MonthPeriod>(monthPeriodParserTemplate);


/**
 * Converts a route param to a number.
 *
 * @throws InvalidRouteParamError If the conversion fails.
 */
export const nullableNumericParser = nullableParserBuilder<number>(numericParserTemplate);


/**
 * Converts a route param to a MonthPeriod.
 *
 * @remarks Since this app is used by spanish speakers, this parser expects
 * `m-Y` formated str.
 *
 * @throws InvalidRouteParamError If the conversion fails.
 */
export const nullableMonthPeriodParser = nullableParserBuilder<MonthPeriod>(monthPeriodParserTemplate);
